import type { OutboxState } from '@jq4race/shared';

/**
 * outbox-item — pure domain model for a single queued check-in (design §5,
 * task 6.1). The Dexie `outbox` table row (see `OutboxRow`,
 * infrastructure/dexie/offline-db.ts) IS an `OutboxItem` at rest; this module
 * owns the type and the pure state-transition function only — no Dexie, no
 * fetch, no side effects.
 */

export interface OutboxLastError {
  code: string;
  status?: number;
  message: string;
  /** Check-in field id for `required_field_missing` (from the error body), to name it in the UI. */
  field?: string;
}

/**
 * UNSYNCED_OUTBOX_STATES — every state that still needs sync or operator
 * attention (everything except terminal `synced`/`discarded`). Shared by
 * `getUnsyncedOutboxCount`, `DexieOutboxRepository.unsynced()`, and the
 * duplicate-plate local-outbox check — a single source of truth so those
 * three call sites can never drift apart on what "still mine to deal with"
 * means (coordinator finding #4's own review focus area).
 */
export const UNSYNCED_OUTBOX_STATES: readonly OutboxState[] = [
  'pending',
  'syncing',
  'rejected',
  'needs-auth',
  'held',
];

export interface OutboxResult {
  ticketId: string;
  ticketNumber: number;
  /**
   * Deliberate, small deviation from the design's literal
   * `result{ticketId,ticketNumber}` schema note: carrying these two optional
   * fields through lets CheckinSuccessScreen (task 6.7) show the same
   * "MEMBER — do not charge" / "SMS sent" confirmations the pre-outbox
   * synchronous flow showed, once a synced result actually lands (Slice 7).
   * Additive and optional — never required, never breaks the Dexie row shape.
   */
  smsDispatched?: boolean;
  coveredByMembership?: boolean;
  /**
   * Fourth adversarial review finding HIGH #1 — set when this item's
   * incident (damage) photos were DROPPED at sync time due to a
   * non-retryable error (most commonly 403 `feature_not_in_plan` — the
   * org's plan lacks `damageReports`, whether it never had the feature or
   * was downgraded between capture and sync) so the ticket could still be
   * created with the 4 mandatory photos rather than blocking on evidence
   * the server will never accept. `undefined` means every incident photo
   * (if any) synced normally. Surfaced to the operator on
   * `CheckinSuccessScreen`.
   */
  incidentPhotosDropped?: OutboxLastError;
  /**
   * pwa-offline-first Slice 7 — deferred finding from Slice 6 round 5 (LOW
   * #6): the server-authoritative `checkedInAt` (design D15's clock-skew
   * correction, possibly clamped by decision 18's hard cap), carried
   * through so it can be shown DURABLY (not just on the transient success
   * screen) — see PendingItemsPage's synced-with-warning section.
   */
  checkedInAt?: string;
  /**
   * `'offline_capped'` when the server clamped this ticket's `checkedInAt`
   * because the claimed capture time fell outside the allowed backdate
   * window (decision 18) — flagged for supervisor review. Absent for the
   * overwhelming majority of tickets (the server's own clock, or an
   * accepted claim within the window).
   */
  checkinTimeSource?: 'offline_verified' | 'offline_capped';
}

export interface OutboxItem {
  /** = clientRequestId (design D1) — the idempotency key AND the Dexie primary key. */
  id: string;
  /** Registers which feature handler (design D7) replays this item at sync — Slice 6 only has 'checkin.create'. */
  kind: string;
  state: OutboxState;
  orgId: string;
  /**
   * Operator who CAPTURED this item — a claimed identity, kept for audit
   * only (design D12: the operator who eventually SYNCS it, possibly a
   * different same-org operator, becomes the ticket's operatorId; this field
   * is never overwritten for that).
   */
  capturedByUserId: string;
  locationId: string;
  /**
   * Fixed ONCE at enqueue time (task 6.1/6.4) — part of the backend
   * idempotency fingerprint (Slice 5). NEVER recompute this on a retry: a
   * different value per attempt would make a lost-response retry look like a
   * genuinely different request and get wrongly rejected as
   * `idempotency_key_reused` instead of replaying.
   */
  capturedAt: string;
  /** `P-`+first 4 hex chars of `id`, uppercased (design D16) — shown on screen only. */
  provisionalCode: string;
  /** The check-in form body (design's "payload" field) — everything CreateTicketRequest needs except photoKeys. */
  payload: Record<string, unknown>;
  /** Local blob ids (DexieBlobStore rows) captured for this item, in angle order. */
  photoRefs: string[];
  /**
   * Third adversarial review finding H3 — local blob ids for the optional,
   * angle-less incident (pre-existing damage) photos, in capture order.
   * Mirrors `photoRefs` exactly (deterministic ids, relinked at enqueue,
   * covered by the SAME photoRefs-membership orphan-cleanup sweep) — NEVER
   * server storage keys directly in the payload the way they were before
   * this finding: those were server tokens with a short (15-min) TTL, so a
   * late sync or a different same-org operator syncing this item could
   * easily land after the token/object was already purged, silently
   * dropping damage evidence with no recovery path.
   */
  incidentPhotoRefs: string[];
  /**
   * Fourth adversarial review, MEDIUM #2 — keys ALREADY uploaded on a prior
   * attempt (mandatory OR incident, keyed by the same ref string used in
   * `photoRefs`/`incidentPhotoRefs`), with the estimated instant the
   * server-side token/object becomes unusable. A retry REUSES a still-valid
   * entry instead of re-uploading the same bytes again — critical on a slow
   * link, where re-uploading everything on every attempt could mean the
   * item never finishes within any per-item deadline. Cleared entirely
   * (`undefined`) when the server rejects a key with `photo_token_invalid`
   * — our own expiry ESTIMATE was wrong (or the token was invalidated some
   * other way), so every cached entry must be treated as untrustworthy, not
   * just the one the server happened to name.
   */
  uploadedPhotoKeys?: Record<string, { key: string; expiresAt: string }>;
  /** Monotonic per-device ordering — FIFO replay order (design). */
  seq: number;
  attempts: number;
  nextAttemptAt: string | null;
  lastError: OutboxLastError | null;
  result: OutboxResult | null;
  createdAt: string;
  updatedAt: string;
  /**
   * MEDIUM fix (adversarial review) — set by `acknowledgeDurableWarning`
   * when the operator dismisses a synced item's durable warning from
   * `/pending`'s "needs a look" section (see `hasDurableWarning`). Never
   * set by `transition()` — this is metadata about operator review, not a
   * state-machine event, so it is written directly via the repository
   * (`DexieOutboxRepository.update`), not through `transition()`.
   */
  acknowledgedAt?: string;
}

export type OutboxEvent =
  | { type: 'sync-start' }
  | { type: 'sync-success'; result: OutboxResult }
  | { type: 'sync-retry'; error: OutboxLastError; nextAttemptAt: string }
  | { type: 'sync-reject'; error: OutboxLastError }
  | { type: 'needs-auth' }
  | { type: 'other-org' }
  | { type: 'login-same-org' }
  | { type: 'edit' }
  | { type: 'force' }
  | { type: 'discard' };

export class InvalidOutboxTransitionError extends Error {
  constructor(state: OutboxState, eventType: OutboxEvent['type']) {
    super(`Cannot apply event "${eventType}" to an outbox item in state "${state}"`);
    this.name = 'InvalidOutboxTransitionError';
  }
}

/**
 * transition — the ONLY place that decides what state an outbox item moves
 * to next (design "Item state machine"). Pure: never mutates `item`, never
 * touches Dexie/fetch/time-of-day beyond stamping `updatedAt`. An event that
 * does not apply to the item's CURRENT state throws
 * `InvalidOutboxTransitionError` rather than silently no-op'ing — callers
 * (the future SyncEngine, RejectedItemSheet, sign-in-resume flow) must never
 * fire an event without knowing it will actually apply.
 */
export function transition(item: OutboxItem, event: OutboxEvent): OutboxItem {
  const updatedAt = new Date().toISOString();

  if (item.state === 'pending') {
    if (event.type === 'sync-start') return { ...item, state: 'syncing', updatedAt };
    if (event.type === 'other-org') return { ...item, state: 'held', updatedAt };
  }

  if (item.state === 'syncing') {
    if (event.type === 'sync-success') {
      return { ...item, state: 'synced', result: event.result, updatedAt };
    }
    if (event.type === 'sync-retry') {
      return {
        ...item,
        state: 'pending',
        attempts: item.attempts + 1,
        lastError: event.error,
        nextAttemptAt: event.nextAttemptAt,
        updatedAt,
      };
    }
    if (event.type === 'sync-reject') {
      return { ...item, state: 'rejected', lastError: event.error, updatedAt };
    }
    if (event.type === 'needs-auth') return { ...item, state: 'needs-auth', updatedAt };
  }

  if (item.state === 'needs-auth' && event.type === 'login-same-org') {
    return { ...item, state: 'pending', updatedAt };
  }

  if (item.state === 'held' && event.type === 'login-same-org') {
    return { ...item, state: 'pending', updatedAt };
  }

  if (item.state === 'rejected') {
    if (event.type === 'edit' || event.type === 'force') {
      return { ...item, state: 'pending', updatedAt };
    }
    if (event.type === 'discard') return { ...item, state: 'discarded', updatedAt };
  }

  throw new InvalidOutboxTransitionError(item.state, event.type);
}
